import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { API } from "../../../constants/apiPath";
import { startLoading, stopLoading } from "../loader/loaderSlice";

const initialState = {
  user: null,
  isLoggedIn: false,
  isLoading: false,
  registrationInfo: null,
  flag: true,
  header: {
    darknavbar: false,
    sideMenuBarOpen: false,
  },
  tags: [],
  category: {
    gender: "women",
    category: "",
    subcategory: "",
    ternarycategory: "",
    selectedsizes: [],
    selectedcolors: [],
    sortby: "newest",
    minValue: 0,
    maxValue: 1000000,
  },
};

const THUNK_METHODS = {
  SIGNIN_USER: "user/signin",
  LOGIN_USER: "user/login",
  LOGOUT_USER: "user/logout",
  CREATE_USER: "user/create",
  VERIFY_USER: "user/verify",
  UPDATE_ADDRESS: "user/address",
  UPDATE_USER: "user/update",
};

export const loginUser = createAsyncThunk(
  THUNK_METHODS.LOGIN_USER,
  async (loginInfo, { dispatch, rejectWithValue }) => {
    try {
      dispatch(startLoading());
      const {
        data: { user, token },
      } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_API_URL}${API.AUTH.LOGIN}`,
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
        data: loginInfo,
      });
      localStorage.setItem("accessToken", token);
      return user;
    } catch (error) {
      console.error(error.message);
      return rejectWithValue("Login failed");
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const createAccount = createAsyncThunk(
  THUNK_METHODS.CREATE_USER,
  async (userInfo, { dispatch, rejectWithValue }) => {
    try {
      dispatch(startLoading());
      const {
        data: { user, token },
      } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_API_URL}${API.AUTH.SIGNUP}`,
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Authorization: userInfo.accessToken,
        },
        data: userInfo.registrationInfo,
      });
      localStorage.setItem("accessToken", token);
      return user;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || error.message
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const refreshUser = createAsyncThunk(
  THUNK_METHODS.VERIFY_USER,
  async (accessToken, { dispatch, rejectWithValue }) => {
    try {
      dispatch(startLoading());
      const {
        data: { user },
      } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_API_URL}${API.AUTH.VERIFICATION}`,
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
        },
      });

      return user;
    } catch (error) {
      console.error(error.message);
      return rejectWithValue("SignUp failed");
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const updateAddress = createAsyncThunk(
  THUNK_METHODS.UPDATE_ADDRESS,
  async (address, { dispatch, rejectWithValue }) => {
    try {
      dispatch(startLoading());
      await axios({
        method: "PUT",
        url: `${process.env.REACT_APP_API_URL}${API.AUTH.ADDRESS}`,
        withCredentials: true,
        data: address,
      });
      return;
    } catch (error) {
      console.error(error.message);
      return rejectWithValue("SignUp failed");
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const logoutUser = createAsyncThunk(
  THUNK_METHODS.LOGOUT_USER,
  async (data, { dispatch, rejectWithValue }) => {
    try {
      dispatch(startLoading());
      const data = await axios({
        method: "DELETE",
        url: `${process.env.REACT_APP_API_URL}${API.AUTH.LOGOUT}`,
        withCredentials: true,
      });
      return false;
    } catch (error) {
      console.error(error.message);
      return rejectWithValue("SignUp failed");
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const updateUser = createAsyncThunk(
  THUNK_METHODS.UPDATE_USER,
  async (userdetails, { dispatch, rejectWithValue }) => {
    try {
      dispatch(startLoading());
      const data = await axios({
        method: "PUT",
        url: `${process.env.REACT_APP_API_URL}${API.AUTH.UPDATE}`,
        withCredentials: true,
        data: userdetails,
      });
      return data.data;
    } catch (error) {
      console.error(error.message);
      return rejectWithValue("SignUp failed");
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setRegistrationInfo: (state, action) => {
      state.registrationInfo = action.payload;
    },
    setNavbarDark: (state, action) => {
      state.header.darknavbar = action.payload;
    },
    setSideMenuBarOpen: (state, action) => {
      state.header.sideMenuBarOpen = action.payload;
    },
    setGender: (state, action) => {
      state.category.gender = action.payload;
    },
    setTags: (state, action) => {
      const tag = action.payload;

      if (state.tags.includes(tag)) {
        // REMOVE – must create a NEW ARRAY
        state.tags = state.tags.filter(t => t !== tag);
      } else {
        // ADD – must return a NEW ARRAY
        state.tags = [...state.tags, tag];
      }
    },
    clearTags: (state, action) => {
      state.tags = [];
    },
    setCategory: (state, action) => {
      state.category.category = action.payload;
    },
    clearCategory: (state, action) => {
      state.category.category = "";
    },
    setSubCategory: (state, action) => {
      state.category.subcategory = action.payload;
    },
    setTernaryCategory: (state, action) => {
      state.category.ternarycategory = action.payload;
    },
    setSelectedSizes: (state, action) => {
      state.category.selectedsizes.push(action.payload);
    },
    removeSelectedSizes: (state, action) => {
      const index = state.category.selectedsizes.indexOf(action.payload);
      state.category.selectedsizes.splice(index, 1);
    },
    clearSelectedSizes: (state, action) => {
      state.category.selectedsizes = [];
    },
    setSelectedColors: (state, action) => {
      state.category.selectedcolors.push(action.payload);
    },
    removeSelectedColors: (state, action) => {
      const index = state.category.selectedcolors.indexOf(action.payload);
      state.category.selectedcolors.splice(index, 1);
    },
    clearSelectedColors: (state, action) => {
      state.category.selectedcolors = [];
    },
    setSortBy: (state, action) => {
      state.category.sortby = action.payload;
    },
    setMinValue: (state, action) => {
      state.category.minValue = action.payload;
    },
    setMaxValue: (state, action) => {
      state.category.maxValue = action.payload;
    },
    resetMinValue: (state, action) => {
      state.category.minValue = action.payload;
    },
    resetMaxValue: (state, action) => {
      state.category.maxValue = action.payload;
    },
    changeFlag: (state) => {
      state.flag = !state.flag;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(createAccount.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createAccount.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isLoggedIn = true;
    });
    builder.addCase(createAccount.rejected, (state) => {
      state.isLoggedIn = false;
    });
    builder.addCase(loginUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isLoggedIn = true;
    });
    builder.addCase(loginUser.rejected, (state) => {
      state.isLoggedIn = false;
    });
    builder.addCase(refreshUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(refreshUser.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isLoggedIn = !!action.payload;
    });
    builder.addCase(refreshUser.rejected, (state) => {
      state.isLoggedIn = false;
    });
    builder.addCase(updateAddress.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateAddress.fulfilled, (state, action) => {
      state.isLoading = !!action.payload;
    });
    builder.addCase(updateAddress.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(updateUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateUser.fulfilled, (state, action) => {
      state.isLoading = !!action.payload;
    });
    builder.addCase(updateUser.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(logoutUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(logoutUser.fulfilled, (state, action) => {
      state.isLoggedIn = action.payload;
      state.user = null;
    });
    builder.addCase(logoutUser.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const {
  setUser,
  setRegistrationInfo,
  setNavbarDark,
  setGender,
  setCategory,
  clearCategory,
  setSubCategory,
  setTernaryCategory,
  setSelectedColors,
  setSortBy,
  setSelectedSizes,
  setTags,
  clearTags,
  removeSelectedSizes,
  clearSelectedSizes,
  removeSelectedColors,
  resetMaxValue,
  resetMinValue,
  clearSelectedColors,
  setMaxValue,
  setMinValue,
  changeFlag,
  setSideMenuBarOpen,
} = authSlice.actions;
